import {
	AppButton,
	AppSplitButton,
	ListItem,
	MenuList,
	TwoPaneMenu,
	TwoPaneMenuCategory,
	usePopoverMotion
} from '@clickguard/design-system'
import Copy01 from '@untitled-ui/icons-react/build/cjs/Copy01'
import DotsHorizontal from '@untitled-ui/icons-react/build/cjs/DotsHorizontal'
import Edit05 from '@untitled-ui/icons-react/build/cjs/Edit05'
import Lock01 from '@untitled-ui/icons-react/build/cjs/Lock01'
import Trash01 from '@untitled-ui/icons-react/build/cjs/Trash01'
import Users01 from '@untitled-ui/icons-react/build/cjs/Users01'
import { Plus } from 'components/icons'
import {
	CSSProperties,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react'
import { createPortal } from 'react-dom'
import { TEMPLATE_SCOPE_OPTIONS } from './rule-options'
import { RuleTemplate, TemplateScope } from './types'
import './template-picker.css'

/** Actions pane width (PA uses the same, from Figma 6716:219203). */
const PANE_WIDTH = 188
/** How long the tray's exit fade plays before the portal unmounts. */
const TRAY_EXIT_MS = 170
/** The DS TwoPaneMenu popover's own width. */
const PANEL_WIDTH = 460
/** DS border-default — read from the token so the ring follows the theme. */
const BORDER = 'rgb(var(--cg-border))'
/** The DS popover panel, portaled to <body>. */
const PANEL_SELECTOR = '[role="dialog"].cg-tp-panel'

interface MenuBox {
	top: number
	height: number
}

/** Snapshotted on close, so the tray's exit can play out after the state is gone. */
interface TrayView {
	template: RuleTemplate
	box: MenuBox
	mode: 'menu' | 'delete'
	cardLeft: number
	trayLeft: number
	cardWidth: number
	cardHeight: number | undefined
	exiting: boolean
}

interface TemplatePickerProps {
	/** Main segment — opens the blank create sheet. */
	onCreate: () => void
	templates: RuleTemplate[]
	/** Chosen template — opens the create sheet prefilled from its draft. */
	onSelect: (template: RuleTemplate) => void
	/** Tray "Edit" — opens the sheet on the template's own draft. */
	onEditTemplate: (template: RuleTemplate) => void
	/** Tray "Make shared" / "Make private" — the server needs to be told WHICH template moved. */
	onToggleVisibility: (template: RuleTemplate) => void
	/** Tray "Delete template", after its in-panel confirmation. */
	onDeleteTemplate: (template: RuleTemplate) => void
}

/** Only what you authored can be renamed, re-scoped or deleted. */
const isOwner = (template: RuleTemplate) => template.author === 'You'

/** What an empty tier says — different for your own and the shared one. */
const EMPTY_COPY: Record<TemplateScope, { title: string; note: string }> = {
	mine: {
		title: 'No personal templates',
		note: 'Save a rule as a template and it appears here.'
	},
	shared: {
		title: 'No shared templates',
		note: 'Templates the workspace shares appear here.'
	}
}

/** The items pane for a tier with nothing in it. Mirrors PA's `ChartsEmptyState`,
 *  but every fill reads from a token and the motion is keyframes in the CSS. */
const EmptyPane = ({ title, note }: { title: string; note: string }) => (
	<div className="cg-tp-empty flex h-full flex-col items-center justify-center gap-4 px-5 text-center">
		<div className="relative flex h-16 w-16 items-center justify-center">
			<span
				aria-hidden
				className="cg-tp-empty-halo absolute inset-0 rounded-2xl bg-[rgb(var(--cg-brand)_/_0.06)]"
			/>
			<span
				aria-hidden
				className="absolute inset-[10px] rounded-xl bg-[rgb(var(--cg-brand)_/_0.1)]"
			/>
			<span className="relative flex h-9 w-9 items-center justify-center rounded-[10px] bg-[rgb(var(--cg-surface))] shadow-sm ring-1 ring-[rgb(var(--cg-border))]">
				<Copy01 className="h-4 w-4 text-[rgb(var(--cg-brand))]" />
			</span>
		</div>
		<div className="space-y-1">
			<p className="text-[15px] font-semibold text-[rgb(var(--cg-text-primary))]">
				{title}
			</p>
			<p className="mx-auto max-w-[220px] text-[13px] leading-5 text-[rgb(var(--cg-text-secondary))] [text-wrap:balance]">
				{note}
			</p>
		</div>
	</div>
)

/**
 * The toolbar's "Add rule" control: a DS `AppSplitButton` whose main segment opens
 * the blank sheet and whose chevron opens this template menu. Same shape as the PA
 * report switcher — a TwoPaneMenu plus a kebab that reveals a THIRD pane, with an
 * underlay supplying the tray and shadow under the DS card.
 */
export const TemplatePicker = ({
	onCreate,
	templates,
	onSelect,
	onEditTemplate,
	onToggleVisibility,
	onDeleteTemplate
}: TemplatePickerProps) => {
	const [open, setOpen] = useState(false)
	// The chevron's own menu — the step in front of the library.
	const [menuOpen, setMenuOpen] = useState(false)
	const menuRef = useRef<HTMLDivElement>(null)
	const chevronMotion = usePopoverMotion(menuOpen, {
		transformOrigin: 'top right'
	})
	const byId = new Map(templates.map((template) => [template.id, template]))

	// Dismiss the chevron menu the way the toolbar's other popovers do.
	useEffect(() => {
		if (!menuOpen) return
		const onDown = (event: MouseEvent) => {
			if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
		}
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setMenuOpen(false)
		}
		document.addEventListener('mousedown', onDown)
		document.addEventListener('keydown', onKey)
		return () => {
			document.removeEventListener('mousedown', onDown)
			document.removeEventListener('keydown', onKey)
		}
	}, [menuOpen])

	// ── Right-edge anchoring ─────────────────────────────────────────────────
	// The DS hangs its popover off the trigger's LEFT edge and only clamps it to the
	// viewport, which puts a menu at the right end of the toolbar off the page.
	const triggerRef = useRef<HTMLSpanElement>(null)
	const [anchorRight, setAnchorRight] = useState<number | null>(null)

	useLayoutEffect(() => {
		if (!open) return
		const measure = () => {
			const trigger = triggerRef.current
			if (trigger) setAnchorRight(trigger.getBoundingClientRect().right)
		}
		measure()
		window.addEventListener('scroll', measure, true)
		window.addEventListener('resize', measure)
		return () => {
			window.removeEventListener('scroll', measure, true)
			window.removeEventListener('resize', measure)
		}
	}, [open])

	// ── Per-template actions pane ────────────────────────────────────────────
	const [trayId, setTrayId] = useState<string | null>(null)
	const [menuBox, setMenuBox] = useState<MenuBox | null>(null)
	const [paneH, setPaneH] = useState<number | null>(null)
	const [trayMode, setTrayMode] = useState<'menu' | 'delete'>('menu')
	const trayRef = useRef<HTMLDivElement>(null)

	const trayTemplate = trayId ? byId.get(trayId) ?? null : null
	// The kebab only exists on templates you own; the write paths re-check anyway.
	const trayIsOwner = trayTemplate ? isOwner(trayTemplate) : false

	const closeTray = useCallback(() => {
		setTrayId(null)
		setTrayMode('menu')
		setMenuBox(null)
		setPaneH(null)
	}, [])

	const measurePanel = useCallback((): MenuBox | null => {
		const panel = document.querySelector<HTMLElement>(PANEL_SELECTOR)
		if (!panel) return null
		// Position from the visual rect, SIZE from the layout box: the DS scales the panel
		// to 0.97 on entry, and a mid-animation rect leaves the tray adrift.
		return {
			top: panel.getBoundingClientRect().top,
			height: panel.offsetHeight
		}
	}, [])

	const openTray = (templateId: string) => {
		const box = measurePanel()
		if (!box) return
		setMenuBox(box)
		setTrayMode('menu')
		setTrayId(templateId)
	}

	useLayoutEffect(() => {
		if (!trayId || !menuBox || !trayRef.current) return
		const h = Math.max(menuBox.height, trayRef.current.offsetHeight)
		if (h !== paneH) setPaneH(h)
	}, [trayId, menuBox, trayMode, paneH])

	// The DS re-anchors its panel on scroll and resize; follow it.
	useEffect(() => {
		if (!trayId) return
		const track = () => {
			const box = measurePanel()
			if (box)
				setMenuBox((prev) => ({ ...box, height: prev?.height ?? box.height }))
		}
		window.addEventListener('scroll', track, true)
		window.addEventListener('resize', track)
		return () => {
			window.removeEventListener('scroll', track, true)
			window.removeEventListener('resize', track)
		}
	}, [trayId, measurePanel])

	// A click back inside the menu closes just the pane; a kebab is ignored so it
	// can re-target.
	useEffect(() => {
		if (!trayId) return
		const onDown = (event: MouseEvent) => {
			const target = event.target as HTMLElement
			if (trayRef.current?.contains(target)) return
			if (target.closest?.('[data-tp-kebab]')) return
			if (target.closest?.(PANEL_SELECTOR)) closeTray()
		}
		// The DS menu registers its OWN document-level Escape handler, so this runs in
		// the capture phase and stops the event dead.
		const onEsc = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return
			event.stopImmediatePropagation()
			closeTray()
		}
		document.addEventListener('mousedown', onDown)
		document.addEventListener('keydown', onEsc, true)
		return () => {
			document.removeEventListener('mousedown', onDown)
			document.removeEventListener('keydown', onEsc, true)
		}
	}, [trayId, closeTray])

	// ── Actions. Delete drops into a sub-panel; Edit opens the rule sheet ──────────
	const doScope = () => {
		if (!trayTemplate || !trayIsOwner) return
		onToggleVisibility(trayTemplate)
		closeTray()
	}
	const doDelete = () => {
		if (!trayTemplate || !trayIsOwner) return
		onDeleteTemplate(trayTemplate)
		closeTray()
	}

	// The muted line is the byline; the description still feeds search.
	const row = (template: RuleTemplate) => {
		const supporting = `Created by ${
			template.author === 'You' ? 'you' : template.author
		}`
		return {
			id: template.id,
			searchText: `${template.name} ${template.description}`,
			label: (
				<span className="flex w-full min-w-0 items-center gap-2">
					<span className="flex min-w-0 flex-1 flex-col">
						<span className="truncate">{template.name}</span>
						<span className="cg-truncate cg-text-text-xs cg-text-text-secondary">
							{supporting}
						</span>
					</span>
					{/* Only on templates you own: on a teammate's the kebab opened a pane
					    of disabled rows. */}
					{isOwner(template) && (
						<span
							role="button"
							tabIndex={-1}
							data-tp-kebab
							data-tray-active={trayId === template.id ? 'true' : undefined}
							aria-label={`Manage ${template.name}`}
							// Only the chip answers the hover, held while the tray is
							// open — otherwise the kebab loses it as the pointer leaves.
							className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[rgb(var(--cg-text-primary))] transition hover:bg-[rgb(var(--cg-selected-hover))] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(var(--cg-brand))] active:scale-90 data-[tray-active]:bg-[rgb(var(--cg-selected-hover))]"
							onClick={(event) => {
								event.stopPropagation()
								openTray(template.id)
							}}
						>
							<DotsHorizontal className="h-4 w-4" />
						</span>
					)}
				</span>
			)
		}
	}

	// A tier with no templates used to hand the DS `items: []`, which draws a blank
	// card, so an empty tier switches to the `content` escape hatch.
	const categories: TwoPaneMenuCategory[] = TEMPLATE_SCOPE_OPTIONS.map(
		(option) => {
			const rows = templates.filter(
				(template) => template.scope === option.value
			)
			const base = { id: option.value, label: option.label }
			return rows.length
				? { ...base, items: rows.map(row) }
				: { ...base, content: <EmptyPane {...EMPTY_COPY[option.value]} /> }
		}
	)

	const trayOpen = Boolean(open && trayTemplate && menuBox)
	const cardHeight = paneH ?? menuBox?.height
	// The card ends at the button's right edge and grows LEFT, by transform rather
	// than layout.
	const cardWidth = PANEL_WIDTH + (trayOpen ? PANE_WIDTH : 0)
	const panelLeft =
		anchorRight === null ? null : Math.max(8, anchorRight - PANEL_WIDTH)
	const cardLeft =
		panelLeft === null ? null : panelLeft - (trayOpen ? PANE_WIDTH : 0)
	const trayLeft = cardLeft === null ? 0 : cardLeft + PANEL_WIDTH

	// ── Tray exit motion — state flips unmount instantly, so the last tray is kept
	//    alive for one beat from a snapshot, only when just the pane closes ─────────
	const [trayExiting, setTrayExiting] = useState(false)
	const traySnapshot = useRef<Omit<TrayView, 'exiting'> | null>(null)
	const wasTrayOpen = useRef(false)

	useLayoutEffect(() => {
		if (trayOpen && trayTemplate && menuBox) {
			traySnapshot.current = {
				template: trayTemplate,
				box: menuBox,
				mode: trayMode,
				cardLeft: cardLeft ?? 0,
				trayLeft,
				cardWidth,
				cardHeight
			}
		}
	})

	useEffect(() => {
		if (trayOpen) {
			wasTrayOpen.current = true
			setTrayExiting(false)
			return
		}
		if (!wasTrayOpen.current) return
		wasTrayOpen.current = false
		if (
			!open ||
			window.matchMedia('(prefers-reduced-motion: reduce)').matches
		) {
			traySnapshot.current = null
			return
		}
		setTrayExiting(true)
		const timeout = setTimeout(() => {
			setTrayExiting(false)
			traySnapshot.current = null
		}, TRAY_EXIT_MS)
		return () => clearTimeout(timeout)
	}, [trayOpen, open])

	const trayView: TrayView | null =
		trayOpen && trayTemplate && menuBox
			? {
					template: trayTemplate,
					box: menuBox,
					mode: trayMode,
					cardLeft: cardLeft ?? 0,
					trayLeft,
					cardWidth,
					cardHeight,
					exiting: false
			  }
			: trayExiting && traySnapshot.current
			? { ...traySnapshot.current, exiting: true }
			: null

	return (
		<>
			<TwoPaneMenu
				trigger={
					// The DS clones the trigger and hangs its own toggle onClick on it, so every
					// segment stops its click short and `open` drives the library.
					<span ref={triggerRef} className="relative inline-flex">
						<AppSplitButton
							variant="primary"
							size="sm"
							label="Add rule"
							leftIcon={<Plus />}
							menuLabel="More ways to add a rule"
							menuOpen={menuOpen || open}
							onClick={(event) => {
								event.stopPropagation()
								setMenuOpen(false)
								setOpen(false)
								closeTray()
								onCreate()
							}}
							onMenuClick={(event) => {
								event.stopPropagation()
								// The DS only fires onOpenChange on its OWN gestures, so the tray is closed by hand.
								setOpen(false)
								closeTray()
								setMenuOpen((prev) => !prev)
							}}
						/>
						{chevronMotion.mounted && (
							<div
								ref={menuRef}
								className="absolute right-0 top-[calc(100%+6px)] z-30"
								style={chevronMotion.style}
								onMouseDown={(event) => event.stopPropagation()}
								onClick={(event) => event.stopPropagation()}
							>
								<MenuList role="menu" style={{ width: 232 }}>
									<ListItem
										label="New rule from template"
										leadingIcon={<Copy01 className="h-4 w-4" />}
										onClick={() => {
											setMenuOpen(false)
											setOpen(true)
										}}
									/>
								</MenuList>
							</div>
						)}
					</span>
				}
				categories={categories}
				onSelectItem={(item) => {
					const template = byId.get(item.id)
					if (template) onSelect(template)
				}}
				open={open}
				onOpenChange={(next) => {
					setOpen(next)
					if (!next) closeTray()
				}}
				// Clicks in the actions pane land outside the DS panel; without this veto they
				// would dismiss the whole menu.
				shouldCloseOnInteractOutside={(target) =>
					!trayRef.current?.contains(target as Node)
				}
				className={`cg-tp-panel${trayOpen ? ' cg-tp-panel--tray' : ''}${
					panelLeft === null ? '' : ' cg-tp-panel--anchored'
				}`}
				// The DS writes its own `left` inline, so the right-edge anchor arrives as a var.
				style={
					{
						'--cg-tp-pane-w': `${PANE_WIDTH}px`,
						...(cardHeight ? { '--cg-tp-pane-h': `${cardHeight}px` } : {}),
						...(panelLeft === null ? {} : { '--cg-tp-left': `${panelLeft}px` })
					} as CSSProperties
				}
				showSearch={false}
				twoTone
				animated
			/>

			{/* Unified card: the underlay carries the tray fill, ring and shadow across all
			    three columns; the DS menu paints on its left two thirds. */}
			{trayView &&
				createPortal(
					<>
						<div
							aria-hidden
							className={`cg-tp-underlay pointer-events-none fixed z-[48] rounded-xl bg-[rgb(var(--cg-canvas))]${
								trayView.exiting ? ' cg-tp-underlay--exit' : ''
							}`}
							style={{
								left: trayView.cardLeft,
								top: trayView.box.top,
								width: trayView.cardWidth,
								height: trayView.cardHeight,
								boxShadow: `0 0 0 1px ${BORDER}, 0px 24px 48px -12px rgba(16, 24, 40, 0.18)`
							}}
						/>
						<div
							ref={trayRef}
							className={`cg-tp-tray fixed z-[49] flex flex-col${
								trayView.exiting ? ' cg-tp-tray--exit' : ''
							}`}
							style={{
								left: trayView.trayLeft,
								top: trayView.box.top,
								width: PANE_WIDTH,
								minHeight: trayView.cardHeight
							}}
						>
							{trayView.mode === 'delete' ? (
								<div
									className="cg-tp-tray-content flex flex-col justify-between p-3"
									style={{ height: trayView.cardHeight }}
								>
									<div className="text-base font-normal text-[rgb(var(--cg-text-primary))]">
										Delete{' '}
										<span className="font-semibold">
											{trayView.template.name}
										</span>
										? This can't be undone.
									</div>
									<div className="flex justify-end gap-2">
										<AppButton
											variant="ghost"
											size="sm"
											label="Cancel"
											onClick={() => setTrayMode('menu')}
										/>
										<AppButton
											variant="primary"
											size="sm"
											label="Delete"
											onClick={doDelete}
										/>
									</div>
								</div>
							) : (
								<div className="cg-tp-tray-content flex h-full flex-col p-2">
									<ListItem
										label="Edit"
										leadingIcon={<Edit05 className="h-4 w-4" />}
										onClick={() => {
											const template = trayView.template
											closeTray()
											setOpen(false)
											onEditTemplate(template)
										}}
									/>
									<ListItem
										label={
											trayView.template.scope === 'mine'
												? 'Make shared'
												: 'Make private'
										}
										leadingIcon={
											trayView.template.scope === 'mine' ? (
												<Users01 className="h-4 w-4" />
											) : (
												<Lock01 className="h-4 w-4" />
											)
										}
										onClick={doScope}
									/>
									<ListItem
										label="Delete template"
										leadingIcon={<Trash01 className="h-4 w-4" />}
										className="cg-tp-delete-item"
										onClick={() => setTrayMode('delete')}
									/>
									<div className="mx-2 my-1 h-px bg-[rgb(var(--cg-border))]" />
									<ListItem
										label={
											<span className="font-medium text-[rgb(var(--cg-brand))]">
												Cancel
											</span>
										}
										onClick={closeTray}
									/>
								</div>
							)}
						</div>
					</>,
					document.body
				)}
		</>
	)
}
